// Slice H2 — EMA Pullback Trend-Follower (curated template v1).
//
// Week 2 schema fix:
//   • SL/TP: switched from `price_source`/`stop_pct` (decorative, ignored
//     by engine) → engine-canonical `stopType: 'percentage'` + `stopValue`.
//   • Entry: switched from `orderType` → `entryType` (canonical engine read);
//     dropped `sizingMethod`/`sizingValue` (engine-unrecognized) — sizing
//     now flows through the canonical max_risk_per_trade_pct_v1 risk_rule
//     below. Engine falls back to a sensible default until the risk_rule
//     resize kicks in.
//   • Risk rule: now uses canonical `__canonical: 'max_risk_per_trade_pct_v1'`
//     marker + executable `code` body + correct `maxRiskPerTradePct`
//     parameter name. Pre-fix the block was a silent no-op.
//
// Strategy logic (unchanged from v1):
//   • Identify uptrend via fast EMA above slow EMA.
//   • Enter long when price closes above the slow EMA AFTER pulling
//     back to/below it (the "pullback").
//   • Exit on fast EMA crossing back below slow EMA (trend break).
//   • 2% percentage stop, 4% take-profit (1:2 risk-reward).
//   • Per-trade risk capped at 1% via canonical max_risk_per_trade_pct_v1.

import { BlockType, type Block } from '../types';
import {
  type CuratedTemplate,
  templateBlockMetadata,
  namespacedBlockId,
} from './types';
import {
  maxRiskPerTradeParameters,
} from './canonicalRiskRuleBodies';

const SLUG = 'trend-ema-pullback';
const id = (name: string) => namespacedBlockId(SLUG, name);
const meta = (desc: string) => templateBlockMetadata(desc);

const blocks: Block[] = [
  // ── Strategy foundation ───────────────────────────────────────────
  {
    id: id('config'),
    type: BlockType.STRATEGY_CONFIG,
    data: {
      name: 'Strategy config',
      initial_capital: 10_000,
      commission_value: 0.06,           // 0.06% — crypto-futures realistic
      commission_type: 'percentage',
      slippage_value: 0.02,             // 2 bps
      slippage_type: 'percentage',
      // S65 (2026-06-06) — normalized to false. See resolveEffectiveConfig.ts
      // for the rationale + trade-off; this matches the new product-wide
      // default. Users can tick it back on in the cfg block.
      process_orders_on_close: false,
    } as Block['data'],
    position: { x: 50, y: 320 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Capital + cost defaults appropriate for crypto futures.'),
  },

  {
    id: id('price'),
    type: BlockType.PRICE_DATA,
    data: { name: 'Price data' } as Block['data'],
    position: { x: 50, y: 80 },
    connections: { inputs: [], outputs: [id('ema-fast-L'), id('ema-slow-L')] },
    metadata: meta('Source candle feed.'),
  },

  // ── Indicators (LONG side — V-10A keeps long/short subgraphs disjoint) ──
  {
    id: id('ema-fast-L'),
    type: BlockType.MOVING_AVERAGE,
    data: {
      name: 'Fast EMA (20)',
      period: 20,
      source: 'close',
      indicator: 'ema',
      smoothing: 2,
    } as Block['data'],
    position: { x: 230, y: 60 },
    connections: { inputs: [id('price')], outputs: [id('expr-fast-above-slow-L')] },
    metadata: meta('Short-horizon EMA — flips first on trend changes.'),
  },

  {
    id: id('ema-slow-L'),
    type: BlockType.MOVING_AVERAGE,
    data: {
      name: 'Slow EMA (50)',
      period: 50,
      source: 'close',
      indicator: 'ema',
      smoothing: 2,
    } as Block['data'],
    position: { x: 230, y: 130 },
    connections: { inputs: [id('price')], outputs: [id('expr-fast-above-slow-L'), id('expr-close-above-slow-L')] },
    metadata: meta('Longer EMA — trend reference + pullback anchor.'),
  },

  // ── Trend filter: fast > slow ────────────────────────────────────
  {
    id: id('expr-fast-above-slow-L'),
    type: BlockType.EXPRESSION,
    data: {
      name: 'Fast > Slow (uptrend)',
      leftSource: 'input',
      leftInputBlockId: id('ema-fast-L'),
      rightSource: 'input',
      rightInputBlockId: id('ema-slow-L'),
      operator: '>',
    } as Block['data'],
    position: { x: 430, y: 60 },
    connections: { inputs: [id('ema-fast-L'), id('ema-slow-L')], outputs: [id('and-entry-L')] },
    metadata: meta('Boolean: are we in an uptrend?'),
  },

  // ── Pullback signal: close above slow EMA (after touching it) ─────
  {
    id: id('expr-close-above-slow-L'),
    type: BlockType.EXPRESSION,
    data: {
      name: 'Close > Slow EMA',
      leftSource: 'price',
      leftPriceSource: 'close',
      leftPriceDataBlockId: id('price'),
      rightSource: 'input',
      rightInputBlockId: id('ema-slow-L'),
      operator: '>',
    } as Block['data'],
    position: { x: 430, y: 130 },
    connections: { inputs: [id('ema-slow-L')], outputs: [id('and-entry-L')] },
    metadata: meta('Boolean: did price re-cross above the pullback anchor?'),
  },

  // ── AND gate — entry trigger when both conditions hold ───────────
  {
    id: id('and-entry-L'),
    type: BlockType.LOGIC_GATE,
    data: {
      name: 'Entry AND',
      gateType: 'AND',
    } as Block['data'],
    position: { x: 630, y: 100 },
    connections: {
      inputs: [id('expr-fast-above-slow-L'), id('expr-close-above-slow-L')],
      outputs: [id('entry-L')],
    },
    metadata: meta('Long entry fires when trend AND pullback both signal.'),
  },

  // ── Terminals ────────────────────────────────────────────────────
  {
    id: id('entry-L'),
    type: BlockType.ENTRY,
    data: {
      name: 'Long entry',
      direction: 'long',
      // Week 2 fix: engine reads `entryType` (not `orderType`).
      entryType: 'market',
      logic: 'AND',
      // Sizing flows through the canonical max_risk_per_trade_pct_v1
      // risk_rule below — it resizes the proposed qty based on the SL
      // distance. The engine's default proposed-qty is consistent across
      // backtests; the risk rule caps it to 1% of accountSize.
      useStrategyDefaults: false,
    } as Block['data'],
    position: { x: 830, y: 100 },
    connections: { inputs: [id('and-entry-L')], outputs: [] },
    metadata: meta('Market long; canonical risk rule resizes against the SL.'),
  },

  {
    id: id('exit-L'),
    type: BlockType.EXIT,
    data: {
      name: 'Exit on trend break',
    } as Block['data'],
    position: { x: 830, y: 200 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Exit when fast EMA crosses back below slow.'),
  },

  {
    id: id('sl'),
    type: BlockType.STOP_LOSS,
    data: {
      name: 'Stop loss (2%)',
      // Week 2 fix: engine reads `stopType` + `stopValue` (per
      // structuralFallback.ts and ClientSideBacktestEngine.ts:2057).
      // Pre-fix shape (`price_source`/`stop_pct`) was decorative and
      // caused silent fallback to engine default (~1%).
      stopType: 'percentage',
      stopValue: 2.0,
      // S4c (2026-06-02): `executionMethod` removed (dead field). See audit doc.
      exitMode: 'full',
    } as Block['data'],
    position: { x: 830, y: 280 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Conservative 2% stop — adjust if asset volatility differs.'),
  },

  {
    id: id('tp'),
    type: BlockType.TAKE_PROFIT,
    data: {
      name: 'Take profit (1:2 risk-reward)',
      // Week 2 fix: tpType + riskRewardRatio (canonical) instead of
      // `price_source`/`tp_pct`. With stopValue=2.0 and rr=2.0 the
      // engine produces a 4% target — same intent as the pre-fix shape.
      tpType: 'risk_reward',
      riskRewardRatio: 2.0,
      // S4c (2026-06-02): `executionMethod` removed (dead field). See audit doc.
    } as Block['data'],
    position: { x: 830, y: 360 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('1:2 risk:reward — pairs with the 2% SL.'),
  },

  // ── Risk control: cap per-trade risk at 1% of equity ─────────────
  // Week 2 fix: now uses the canonical __canonical marker + the engine-
  // recognized body. Pre-fix the block was a silent no-op (wrong
  // parameter field names + no executable code).
  {
    id: id('risk-max-per-trade'),
    type: BlockType.CUSTOM,
    data: {
      name: 'Max risk per trade (1%)',
      blockType: 'risk_rule',
      // TSL migration: the vestigial JS body is removed. The engine enforces
      // this rule via the `__canonical` marker on the parameters (trusted-TS
      // dispatch in riskRules.ts), so no executable strategy code is carried.
      parameters: maxRiskPerTradeParameters(1.0, 'resize'),
      // S4d (2026-06-02): `outputTypes` removed (dead field). See audit doc.
    } as Block['data'],
    position: { x: 50, y: 480 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Per-trade risk capped at 1% of equity (canonical).'),
  },
];

export const TREND_EMA_PULLBACK: CuratedTemplate = {
  slug: SLUG,
  name: 'EMA Pullback Trend-Follower (v1)',
  family: 'trend',
  bestFor: 'Trades pullbacks to the slow EMA inside a confirmed uptrend.',
  detail:
    'A classic trend-following starter. Confirms direction with fast-EMA-above-slow-EMA, then ' +
    'enters long when price closes back above the slow EMA — the canonical "pullback to support" ' +
    'pattern. Exits on trend break (fast crossing below slow), with a 2% hard stop and 4% take-profit. ' +
    'Per-trade risk capped at 1% via the canonical max_risk_per_trade_pct rule.',
  assetGuidance:
    'Best on assets with clear directional persistence: BTC/ETH on higher timeframes, equity ' +
    'indices on daily. Avoid choppy ranges — the EMA cross will whipsaw.',
  timeframeGuidance: ['1h', '4h', '1d'],
  riskNotes: [
    'Fragile in chop — expect whipsaws when fast/slow oscillate',
    'Expects directional bias; underperforms in range markets',
    'Fixed 2% stop may be too tight on high-volatility assets',
  ],
  expectedValidation:
    'Green on clearly-trending samples (bull cycles); yellow in mixed regimes; ' +
    'red+tail_fail in range-bound holdout windows.',
  blocks,
  version: 'v1',
};
